import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { LINKS } from "@/lib/site";

const EASE = [0.16, 1, 0.3, 1];
const HEAD_LINES = ["Turning Ambition", "Into Achievement."];

function GrowthIllustration() {
  return (
    <svg
      viewBox="0 0 520 520"
      fill="none"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* subtle grid */}
      {[...Array(6)].map((_, i) => (
        <line
          key={`h${i}`}
          x1="40"
          y1={80 + i * 72}
          x2="480"
          y2={80 + i * 72}
          stroke="#FFFFFF"
          strokeOpacity="0.06"
        />
      ))}
      {[...Array(6)].map((_, i) => (
        <line
          key={`v${i}`}
          x1={80 + i * 72}
          y1="40"
          x2={80 + i * 72}
          y2="480"
          stroke="#FFFFFF"
          strokeOpacity="0.06"
        />
      ))}

      {/* faint area under curve */}
      <motion.path
        d="M56 452 C 160 452, 190 300, 268 260 C 340 224, 372 150, 468 84 L 468 452 Z"
        fill="#C8A75D"
        fillOpacity="0.06"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 1 }}
      />

      {/* main growth curve */}
      <motion.path
        d="M56 452 C 160 452, 190 300, 268 260 C 340 224, 372 150, 468 84"
        stroke="#C8A75D"
        strokeWidth="3"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, ease: EASE, delay: 0.6 }}
      />

      {/* nodes */}
      {[
        [56, 452],
        [268, 260],
        [468, 84],
      ].map(([cx, cy], i) => (
        <motion.g
          key={i}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: EASE, delay: 1.4 + i * 0.25 }}
          style={{ transformOrigin: `${cx}px ${cy}px` }}
        >
          <circle cx={cx} cy={cy} r="16" fill="#C8A75D" fillOpacity="0.15" />
          <circle cx={cx} cy={cy} r="6" fill="#C8A75D" />
        </motion.g>
      ))}

      {/* arrow head at the top */}
      <motion.path
        d="M448 84 L468 84 L468 104"
        stroke="#C8A75D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4 }}
      />

      {/* floating accent squares */}
      <motion.rect
        x="120" y="120" width="34" height="34" rx="8"
        stroke="#FFFFFF" strokeOpacity="0.18"
        animate={{ y: [120, 108, 120] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.rect
        x="380" y="300" width="24" height="24" rx="6"
        fill="#C8A75D" fillOpacity="0.25"
        animate={{ y: [300, 314, 300] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      />
    </svg>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yArt = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative overflow-hidden bg-[#0B1F3A] grain"
    >
      {/* ambient light */}
      <div
        className="pointer-events-none absolute -top-40 right-0 h-[560px] w-[560px] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #C8A75D, transparent 70%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-36 pb-24 lg:pt-44 lg:pb-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <motion.div style={{ y: yText, opacity: fade }} className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-px w-8 bg-[#C8A75D]" />
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#C8A75D]">
                Nexfold Consults
              </span>
            </motion.div>

            <h1 className="font-display font-extrabold tracking-tight text-white text-5xl md:text-6xl lg:text-7xl leading-[1.04]">
              {HEAD_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden py-[0.04em]">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 1, ease: EASE, delay: 0.35 + i * 0.15 }}
                  >
                    {i === 1 ? (
                      <>
                        Into <span className="text-[#C8A75D]">Achievement.</span>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-white/70"
            >
              At Nexfold, we help students, professionals, entrepreneurs, and
              business owners navigate important career and business decisions with
              confidence. From launching a business to building a successful career,
              we provide practical guidance tailored to your ambitions.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href={LINKS.instagramDM}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-book-consultation-hero"
                className="group inline-flex items-center gap-2 rounded-full bg-[#C8A75D] px-8 py-4 text-sm font-semibold text-[#0B1F3A] transition-colors duration-300 hover:bg-white"
              >
                Book a Consultation
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href="#services"
                data-testid="cta-explore-services"
                className="group inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:border-[#C8A75D] hover:text-[#C8A75D]"
              >
                Explore Services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            style={{ y: yArt }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.4 }}
            className="lg:col-span-5"
          >
            <div className="relative rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm">
              <div className="aspect-square">
                <GrowthIllustration />
              </div>
              <div className="absolute left-6 bottom-6 rounded-xl border border-white/10 bg-[#0B1F3A]/70 px-4 py-3 backdrop-blur">
                <p className="font-display text-2xl font-extrabold text-white">+43%</p>
                <p className="text-[11px] uppercase tracking-[0.18em] text-white/50">
                  Growth trajectory
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
